import React, { FunctionComponent, useEffect } from 'react'
import { Trans, useTranslation } from '../../utils/convergence'
import { Text, DesignLanguage, useIsDesignLanguage, useDesignLanguagePerClass } from '@afterpaytouch/core'
import { TermLink } from '../TermLink'
import {
  CheckoutState,
  setConfirmTermsViewed,
  useAppDispatch,
  useApplePaylaterLinkParams,
  useAppRouter,
  useCheckoutState,
  useCreditScoreDisclosureDocumentId,
  useIsConsumerLendingAutopayEnabled,
  useIsPaymonthlySUPOrder,
  useLoanAgreementDocumentId,
  usePaymentAuthorisationDocumentId,
  useShowPayMonthlySummaryFebConsent,
} from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import styles from './style.module.css'
import { useMoney } from '../Money/useMoney'

interface TermsProps {
  setDocumentIdToShow: (documentId: string, isTilDisclosure?: boolean) => void
}

export const Terms: FunctionComponent<TermsProps> = ({ setDocumentIdToShow }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const state: CheckoutState = useCheckoutState()
  const autopayEnabled = useIsConsumerLendingAutopayEnabled()
  const paymentAuthorisationDocumentId = usePaymentAuthorisationDocumentId()
  const loanAgreementDocumentId = useLoanAgreementDocumentId()
  const creditScoreDisclosureDocumentId = useCreditScoreDisclosureDocumentId()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()

  const isSupPMOrder = useIsPaymonthlySUPOrder()
  const showFebPrivacyClause = useShowPayMonthlySummaryFebConsent() && !isSupPMOrder
  const orderAmount = state.started?.consumerAmount ?? state.checkout?.amount

  const { formatMoney } = useMoney({
    locale,
    currency: orderAmount?.currency,
  })

  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const linkColor = isArcadeDesignLanguage ? 'Black' : 'Orca'
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const withApplePaylaterParams = useApplePaylaterLinkParams()

  const components = {
    autoPayAuthorization: (
      <TermLink
        key='til-autopay-authorisation'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='auto-payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_PAYMENT_AUTHORISATION_LINK)
          setDocumentIdToShow(paymentAuthorisationDocumentId)
        }}
      />
    ),
    truthInLendingDisclosure: (
      <TermLink
        key='til-disclosure'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='payment-authorisation-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_TRUTH_IN_LENDING_DISCLOSURE_LINK)
          setDocumentIdToShow(loanAgreementDocumentId, true)
        }}
      />
    ),
    loanAgreement: (
      <TermLink
        key='til-loan-agreement'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='loan-agreement-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_LOAN_AGREEMENT_LINK)
          setDocumentIdToShow(loanAgreementDocumentId)
        }}
      />
    ),
    creditScoreDisclosure: (
      <TermLink
        key='til-credit-score-disclosure'
        href=''
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='credit-score-disclosure-link'
        onClick={(e) => {
          e.preventDefault()
          logEvent(TrackingEvent.PRESSED_CL_TRUTH_IN_LENDING_PAGE_CREDIT_SCORE_DISCLOSURE_LINK)
          setDocumentIdToShow(creditScoreDisclosureDocumentId)
        }}
      />
    ),
    cardAccountAgreement: (
      <TermLink
        key='til-card-account-agreement'
        href={withApplePaylaterParams(t('consumerLending:til:cardAccountAgreementUrl'))}
        color={linkColor}
        fontFace='CashSansMono'
        testNameSpace='card-account-agreement-link'
        target='_blank'
      />
    ),
    ...(showFebPrivacyClause && {
      febPrivacy: (
        <TermLink
          key='feb-privacy-policy'
          href={withApplePaylaterParams(t('consumerLending:febPrivacyPolicyUrl'))}
          color={linkColor}
          fontFace='CashSansMono'
          testNameSpace='feb-privacy-policy-link'
          target='_blank'
          onClick={() => logEvent(TrackingEvent.PRESSED_PAY_MONTHLY_SUMMARY_FEB_CONSENT_LINK)}
        />
      ),
    }),
    br: <br />,
  }

  const getTransContext = (): string => {
    let context: string
    if (isSupPMOrder) {
      context = autopayEnabled ? 'supAutopay' : 'sup'
    } else {
      context = autopayEnabled ? 'noTilAutopay' : 'noTil'
    }
    return showFebPrivacyClause ? `${context}Feb` : context
  }

  const i18nKey = 'consumerLending:til:terms'
  const context = getTransContext()

  useEffect(() => {
    dispatch(setConfirmTermsViewed(`${i18nKey}_${context}`))
  }, [i18nKey, context])

  useEffect(() => {
    if (showFebPrivacyClause) {
      logEvent(TrackingEvent.VIEWED_PAY_MONTHLY_SUMMARY_FEB_CONSENT)
    }
  }, [showFebPrivacyClause])

  return (
    <div className={designLanguageStyles.termsContainer}>
      <Text color='Gray40' size={isArcadeDesignLanguage ? 'XXS' : 'XS'} fontFace='CashSansMono'>
        <Trans
          key={context}
          i18nKey={i18nKey}
          components={components}
          context={context}
          values={{
            supAmount: formatMoney(orderAmount?.amount),
          }}
        />
      </Text>
    </div>
  )
}
